/****************************************************
 * Module Name:  pricing.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/
import { Box, Tooltip, Button, Typography, Drawer, Select, FormControl, MenuItem, InputLabel, FormControlLabel, Switch, IconButton } from "@mui/material";
import React, { useState, useEffect } from "react";
import CommonSnackbar from "../common/commonSnackBar";
import Constants from "../../utility/constants";
import { DataGridPro, GridToolbar, gridClasses, GridFilterInputValue, getGridStringOperators, getGridSingleSelectOperators } from "@mui/x-data-grid-pro";
import CircularProgress from "@mui/material/CircularProgress";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import renderCellExpand from "../common/dataGridCellExpand";
import NivodaUtilityProxy from "../../utility/proxy/nivodautility/nivodautilityProxy";
import Utility from "../../utility/utility";
import EditIcon from '@mui/icons-material/Edit';
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";
import NoPermission from "../common/noPermission";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import CommonStoneSelection from "../common/commonStoneSelection";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextField from "@mui/material/TextField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import StoneProxy from "../../utility/proxy/manager/stoneProxy";
import TuneIcon from '@mui/icons-material/Tune';
import StockSearchCriteria from "../operations/stockSearchCriteria";
import StoneDetailModal from "../common/stoneDetailModal";
import NivodaProxy from "../../utility/proxy/manager/nivodaProxy";
import { styled } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import { BackupTable } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import Papa from 'papaparse';
import dayjs from "dayjs";
import { Form, Modal } from "react-bootstrap";
import logo from '../../images/pansuriya.jpg';
import SaveIcon from '@mui/icons-material/Save';
import AddTaskIcon from '@mui/icons-material/AddTask';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import StoneSelectionModal from "../common/stoneSelectionModal";
import ConfirmationModal from "../common/confirmationModal";
import KapanHistoryModal from "../manufacturing/operations/kapanHistoryModal";
import PacketHistoryModal from "../common/packetHistoryModal";
import PageTitle from "../common/pageTitle";

const useStyles = makeStyles({
    lavenderCell: {
        color: '#080808',
        backgroundColor: '#D8BFD8',
    },
    greenCell: {
        color: '#080808',
        backgroundColor: '#B6D7A8',
    },
    redCell: {
        color: '#080808',
        backgroundColor: '#f5d7d7',
    },
    yellowCell: {
        color: '#080808',
        backgroundColor: '#F1E68C',
    },
})

const StyledDataGridPro = styled(DataGridPro)({
    '.MuiDataGrid-columnHeader .MuiDataGrid-sortIcon': {
        display: 'none',
    },
    '.MuiDataGrid-menuIcon': {
        width: '1px',
        fontSize: '1px'
    },
    [`& .${gridClasses.menuIconButton}`]: {
        marginRight: '-2px',
        fontSize: '1px',
        width: '1px',
        height: '1px',
    }
})

const NewStoneSwitch = styled((props) => (
    <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
    width: 42,
    height: 26,
    padding: 0,
    top: "3px",
    '& .MuiSwitch-switchBase': {
        padding: 0,
        margin: 2,
        transitionDuration: '300ms',
        '&.Mui-checked': {
            transform: 'translateX(16px)',
            color: '#fff',
            '& + .MuiSwitch-track': {
                backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#65C466',
                opacity: 1,
                border: 0,
            },
            '&.Mui-disabled + .MuiSwitch-track': {
                opacity: 0.5,
            },
        },
        '&.Mui-focusVisible .MuiSwitch-thumb': {
            color: '#33cf4d',
            border: '6px solid #fff',
        },
        '&.Mui-disabled .MuiSwitch-thumb': {
            color:
                theme.palette.mode === 'light'
                    ? theme.palette.grey[100]
                    : theme.palette.grey[600],
        },
        '&.Mui-disabled + .MuiSwitch-track': {
            opacity: theme.palette.mode === 'light' ? 0.7 : 0.3,
        },
    },
    '& .MuiSwitch-thumb': {
        boxSizing: 'border-box',
        width: 22,
        height: 22,
    },
    '& .MuiSwitch-track': {
        borderRadius: 26 / 2,
        backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
        opacity: 1,
        transition: theme.transitions.create(['background-color'], {
            duration: 500,
        }),
    },
}));


const Pricing = () => {

    const { apiRef, handleCellClick } = Utility.useSingleClickEdit();

    const [errorSnackbar, setErrorSnackbar] = useState(false);
    const [pricingListLoader, setPricingListLoader] = useState(false);
    const [pricingList, setPricingList] = useState([]);
    const [stoneList, setStoneList] = useState([]);
    const [selectionModel, setSelectionModel] = useState([]);
    const [snackBarMessage, setSnackBarMessage] = useState("");
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [ApplySuccessSnackbar, setApplySuccessSnackbar] = useState(false);
    const [ApplyRefetchSuccessSnackbar, setApplyRefetchSuccessSnackbar] = useState(false);
    const [uploadResponse, setUploadResponse] = useState("");
    const [disableSubmit, setDisableSubmit] = useState(true);
    const [disableApply, setDisableApply] = useState(true);
    const [disableFetchCriteria, setDisableFetchCriteria] = useState(true);
    const [ApplySubmitSuccessSnackbar, setApplySubmitSuccessSnackbar] = useState(false);
    const [access, setAccess] = useState(false);
    const [toDate, setToDate] = useState(new Date());
    const [drawerExpanded, setDrawerExpanded] = useState(true);
    const [stockCriteriaState, setStockCriteriaState] = useState('');
    const [searchCriteria, setSearchCriteria] = useState("");
    const [selectedIds, setSelectedIds] = useState([]);
    const [stoneId, setStoneId] = useState("");
    const [showStoneDetailModal, setShowStoneDetailModal] = useState(false);
    const [columns, setColumns] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [], });
    const [alertSnackbar, setAlertSnackbar] = useState(false);
    const [alertMessage, setAlertMessage] = useState("");
    const [uploadSuccessSnackbar, setUploadSuccessSnackbar] = useState(false);
    const [uploadLoader, setUploadLoader] = useState(false);
    const [shapes, setShapes] = useState([]);
    const [colors, setColors] = useState([]);
    const [clarity, setClarity] = useState([]);
    const [cut, setCut] = useState([]);
    const [pol, setPol] = useState([]);
    const [sym, setSym] = useState([]);
    const [flo, setFlo] = useState([]);
    const [tinge, setTinge] = useState([]);
    const [milky, setMilky] = useState([]);
    const [stonetype, setStoneType] = useState([]);
    const [location, setLocation] = useState([]);
    const [lab, setLab] = useState([]);
    const [applyValue, setApplyValue] = useState(0);
    const [applyToAll, setApplyToAll] = useState("newexpposition");
    const [filteredPricingList, setFilteredPricingList] = useState([]);
    const [submitTotal, setSubmitTotal] = useState(false);
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const [confirmModalMessage, setConfirmModalMessage] = useState("");
    const [confirmModalTitle, setConfirmModalTitle] = useState("");

    const [submitLoader, setSubmitLoader] = useState(false);
    const [refetchLoader, setRefetchLoader] = useState(false);
    const [applyLoader, setApplyLoader] = useState(false);
    const [loadingButton, setLoadingButton] = useState(false);
    const [disableAllButtons, setDisableAllButtons] = useState(false);
    const [mumidPermission, setMumidPermission] = useState(false);
    const [suratidPermission, setSuratidPermission] = useState(false);

    const [showGetStoneDataModal, setShowGetStoneDataModal] = useState(false);
    const [stoneIds, setStoneIds] = useState([]);
    const [showStoneSelectionModal, setShowStoneSelectionModal] = useState(false);

    const [showPacketDetailModal, setShowPacketDetailModal] = useState(false);
    const [showKapanDetailModal, setShowKapanDetailModal] = useState(false);
    const [packetId, setPacketId] = useState("");
    const [kapanId, setKapanId] = useState("");

    useEffect(() => {
        if (pricingListLoader || submitLoader || refetchLoader || applyLoader) {
            setDisableAllButtons(true);
        } else {
            setDisableAllButtons(false);
        }
    }, [pricingListLoader, submitLoader, refetchLoader, applyLoader]);

    useEffect(() => {
        PageTitle("ANALYSIS_NIVODA_PRICING");
        checkPermission();
    }, []);

    useEffect(() => {
        setDisableSubmit(selectionModel.length > 0 ? false : true);
        setDisableApply(selectionModel.length > 0 ? false : true);
        setDisableFetchCriteria(selectionModel.length > 0 ? false : true);
    }, [selectionModel]);

    useEffect(() => {
        if (selectedIds.length > 0) {
            handleGetStoneData(selectedIds);
        } else {
            const stonePricingList = stoneList.map(stone => stone.stoneid);
            getPricingData(stonePricingList, stoneList);
        }
    }, [toDate]);

    useEffect(() => {
        const savedColumnOrder = JSON.parse(localStorage.getItem('colOrder'));
        const originalColumns = initialColumns(stoneList);

        if (savedColumnOrder) {
            const reorderedColumns = savedColumnOrder.map(savedColumn => {
                const originalColumn = originalColumns.find(column => column.field === savedColumn);
                return { ...originalColumn };
            });
            setColumns(reorderedColumns);
        } else {
            setColumns(originalColumns);
        }
    }, [stoneList, stonetype]);

    const checkPermission = () => {
        let allowed = Constants.USER_PERMISSION.find(
            (iPermission) =>
                iPermission.permissionid ===
                Constants.PERMISSIONS.ANALYSIS_NIVODA_PRICING
        ) !== undefined
        if (!allowed) {
            setAccess(true)
            return <NoPermission />
        }
    }

    const fetchLiveStoneData = (data, stockCriteriaStored) => {
        setSelectedIds([]);
        setSubmitTotal(false);
        setApplyValue(0);
        setApplyToAll("newexpposition");
        setSearchCriteria(data);
        setStockCriteriaState(stockCriteriaStored);
        let activeTabValue = JSON.parse(stockCriteriaStored).activeTab;
        setPricingListLoader(true);
        const uniqueValues = {
            shape: new Set(),
            color: new Set(),
            clarity: new Set(),
            cut: new Set(),
            pol: new Set(),
            sym: new Set(),
            flo: new Set(),
            tinge: new Set(),
            milky: new Set(),
            location: new Set(),
            lab: new Set(),
        };
        data.status = [];
        data.status.push('AVAILABLE');
        data.status.push('HOLD');
        data.status.push('MEMO_OUT');

        StoneProxy.GetFilteredStock(data, (responseData) => {
            if (responseData) {
                const updatedStoneList = responseData.map((record, idx) => {
                    Object.keys(uniqueValues).forEach(key => {
                        if (record[key] !== null && record[key] !== "") {
                            uniqueValues[key].add(record[key]);
                        }
                    });

                    return { ...record, id: idx, stonetype: Constants.STONE_TYPE[record.stonetype] };
                });
                prepareColumns();

                setShapes([...uniqueValues.shape]);
                setColors([...uniqueValues.color]);
                setClarity([...uniqueValues.clarity]);
                setCut([...uniqueValues.cut]);
                setPol([...uniqueValues.pol]);
                setSym([...uniqueValues.sym]);
                setFlo([...uniqueValues.flo]);
                setTinge([...uniqueValues.tinge]);
                setMilky([...uniqueValues.milky]);
                setLocation([...uniqueValues.location]);
                setLab([...uniqueValues.lab]);

                setStoneList(updatedStoneList);
                const stonePricingList = updatedStoneList.map(stone => stone.stoneid);
                getPricingData(stonePricingList, updatedStoneList);
                setStoneType(activeTabValue === "Natural" ? ["Natural"] : Constants.uniqueFilterValues.stonetype.filter(option => option.name !== 'N').map(option => option.fullname));
            } else {
                setErrorSnackbar(true);
                setPricingListLoader(false);
            }
        });
    };

    const prepareColumns = () => {
        Constants.USER_COLUMN_PERMISSION.forEach(iColumnPermission => {
            if (iColumnPermission === "e24542ae-7b98-44f2-82be-09dfa2dd850b") {
                setMumidPermission(true);
            }
            if (iColumnPermission === "7a913460-41e0-441d-85b9-1f33d71416fb") {
                setSuratidPermission(true);
            }
        })
    };

    const RefetchCriteria = (criteriaList) => {
        NivodaUtilityProxy.RefetchCriteria(criteriaList, (response) => {
            if (response) {
                fetchLiveStoneData(searchCriteria, stockCriteriaState)
                setRefetchLoader(false);
                setApplyRefetchSuccessSnackbar(true);
                setUploadResponse('success');
                setSelectionModel([]);
            }
            else {
                setErrorSnackbar(true);
                setRefetchLoader(false);
                setApplyRefetchSuccessSnackbar(true);
                setUploadResponse('error');
            }
        })
    }

    const getPricingData = (stonePricingList, stoneList) => {
        setPricingListLoader(true);
        const dateParam = Utility.ConvertLocalTimeToUtc(toDate).slice(0, 11) + "23:59:59.999Z";
        NivodaUtilityProxy.GetPricingData(
            stonePricingList, dateParam,
            (responseData) => {
                if (responseData) {
                    responseData.map((record, idx) => {
                        return { ...record, id: idx };
                    });
                    dataProcessing(responseData, stoneList);
                } else {
                    setErrorSnackbar(true);
                    setPricingListLoader(false);
                    setUploadResponse("error");
                }
            }
        );
    }

    const dataProcessing = (responseData, stoneList) => {
        const pricingList = stoneList.map((stone, idx) => {
            const record = responseData.find(rec => String(rec.stoneid) === String(stone.stoneid));
            let formattedRecord = { ...stone, id: idx };

            formattedRecord.newtotal = 0.00.toFixed(2);
            formattedRecord.newexpposition = formattedRecord.expposition;

            if (record) {
                formattedRecord = { ...formattedRecord, ...record };
                if (record.priceList && record.priceList.length > 0) {
                    record.priceList.forEach(priceItem => {
                        formattedRecord[`position${priceItem.position}`] = `${priceItem.price}(${priceItem.noOfStones})`;

                        if (formattedRecord.expposition > 0 && formattedRecord.expposition <= formattedRecord.priceList.length) {
                            formattedRecord.newtotal = ((formattedRecord.priceList[formattedRecord.expposition - 1].price) - 1).toFixed(2);
                        } else if (formattedRecord.expposition > formattedRecord.priceList.length) {
                            formattedRecord.newtotal = ((formattedRecord.priceList[formattedRecord.priceList.length - 1].price) + 1).toFixed(2);
                        }

                        const totalValue = parseFloat(formattedRecord.total);
                        const actualPositionIndex = record.priceList.findIndex(priceItem => totalValue <= priceItem.price);
                        if (actualPositionIndex !== -1 && totalValue === (record.priceList[actualPositionIndex].price).toFixed(2)) {
                            formattedRecord.actualposition = actualPositionIndex;
                        } else {
                            formattedRecord.actualposition = (actualPositionIndex === -1) ? record.priceList.length + 1 : actualPositionIndex + 1;
                        }
                        formattedRecord.percentchange = (((formattedRecord.newtotal * 100) / (formattedRecord.total)) - 100).toFixed(2);
                    });
                }
            }
            return formattedRecord;
        });

        const filteredList = pricingList
            .filter(item => (item.expposition === null || item.expposition === undefined) && (item.priceList !== null && item.priceList.length > 0))
            .map(item => ({
                stoneid: item.stoneid,
                newtotal: item.newtotal,
                newexpposition: item.newexpposition,
                actualposition: item.actualposition,
                minppc: item.minppc,
                stonetype: item.stonetype,
            }));

        if (filteredList.length > 0) {
            NivodaProxy.updateStones(filteredList, (response) => {
                if (response) {
                    const updatedPricingList = pricingList.map(item => {
                        if (item.expposition === null || item.expposition === undefined) {
                            return {
                                ...item,
                                expposition: item.actualposition,
                                newexpposition: item.actualposition
                            };
                        }
                        return item;
                    });
                    setPricingList(updatedPricingList);
                } else {
                    setPricingList(pricingList);
                }
            })
        }
        else {
            setPricingList(pricingList);
        }
        setPricingListLoader(false);
        setUploadResponse("success");
    }

    const getPricingDataByIds = (stonePricingList, callback) => {
        const dateParam = Utility.ConvertLocalTimeToUtc(toDate).slice(0, 11) + "23:59:59.999Z";
        NivodaUtilityProxy.GetPricingDataByIds(
            stonePricingList, dateParam,
            (responseData) => {
                if (responseData) {
                    const transformedData = responseData.map(stone => ({
                        ...stone,
                        stonetype: stone.criteriastonetype,
                    }));
                    callback(transformedData);
                } else {
                    console.error('Error fetching data for missing stones');
                    callback([]);
                }
            }
        )
    }

    const processRowUpdate = (newR, oldRow) => {
        const changedFeilds = Utility.getChangedFields(newR, oldRow);
        if (changedFeilds.length > 0) {
            const { id, field, value } = changedFeilds[0];
            let newRow = { ...oldRow };
            const updatedListData = pricingList.map(row => {
                if (row.stoneid === id) {
                    if (field === "newexpposition" && parseInt(value) > 0 && parseInt(value) !== parseInt(row.newexpposition)) {
                        newRow = {
                            ...row,
                            [field]: Number(value),
                        };

                        const newexpposition = parseInt(value);
                        const stone = pricingList.find(item => item.stoneid === id);
                        let newTotal = stone.newtotal;
                        if (stone && stone.priceList && stone.priceList.length > 0) {
                            if (newexpposition > 0 && newexpposition <= stone.priceList.length) {
                                newTotal = (stone.priceList[newexpposition - 1].price) - 1;
                            } else if (newexpposition > stone.priceList.length) {
                                newTotal = (stone.priceList[stone.priceList.length - 1].price) + 1;
                            }
                            newRow.newtotal = newTotal.toFixed(2);
                            newRow.percentchange = (((newRow.newtotal * 100) / (newRow.total)) - 100).toFixed(2);
                        }
                    }
                    if (field === "newtotal" && parseFloat(value) !== parseFloat(row.newtotal)) {
                        newRow = {
                            ...row,
                            [field]: parseFloat(value).toFixed(2),
                        };

                        const newTotal = parseFloat(value);
                        const stone = pricingList.find(item => item.stoneid === id);
                        if (stone && stone.priceList && stone.priceList.length > 0) {
                            let closestPosition = null;

                            for (const priceItem of stone.priceList) {
                                const price = parseFloat(priceItem.price);
                                if (newTotal < price) {
                                    closestPosition = priceItem.position;
                                    break;
                                } else {
                                    closestPosition = priceItem.position + 1;
                                }
                            }
                            newRow.newexpposition = closestPosition;
                            newRow.percentchange = (((newRow.newtotal * 100) / (newRow.total)) - 100).toFixed(2);
                        }
                    }
                    if (field === 'minppc' && parseFloat(value) !== parseFloat(row.minppc)) {
                        newRow = {
                            ...row,
                            [field]: parseFloat(value).toFixed(2),
                        };
                    }
                    return newRow;
                }
                return row;
            });
            setPricingList(updatedListData);
            return newRow;
        } else {
            // No changes detected, return the newRow to trigger exit edit mode
            return { ...newR };
        }
    }

    const handleClose = () => {
        setApplySuccessSnackbar(false)
        setApplySubmitSuccessSnackbar(false)
        setApplyRefetchSuccessSnackbar(false);
        setShowStoneDetailModal(false);
        setUploadSuccessSnackbar(false);
    };

    const handleIfSubmit = () => {
        if (submitTotal) {
            setShowConfirmationModal(true);
            setConfirmModalMessage("Are you sure you want to save the new total ?");
            setConfirmModalTitle("Save New Total");
        }
        else
            handleSubmit();
    }

    const handleSubmit = () => {
        setSubmitLoader(true);
        let data = [];
        let invalidDataStoneIds = [];
        selectionModel.map((stoneid) => {
            const stoneMatchedRow = stoneList.find(data => String(data.stoneid) === String(stoneid))
            const matchedRow = pricingList.find(data => String(data.stoneid) === String(stoneid))

            if (matchedRow && stoneMatchedRow) {
                let newTotal = parseFloat(matchedRow.newtotal) === 0.00 ? null : matchedRow.newtotal;
                let newExpPosition = matchedRow.newexpposition === null ? null : matchedRow.newexpposition;
                let actualPosition = (matchedRow.actualposition === null || matchedRow.actualposition === undefined) ? null : matchedRow.actualposition;
                let newppc = Number((Number(newTotal) / stoneMatchedRow.size).toFixed(2));
                let minppc = stoneMatchedRow.minppc === null ? null : Number(stoneMatchedRow.minppc.toFixed(2));

                let minppcChanged = matchedRow.minppc === null ? false : (minppc !== matchedRow.minppc);
                let total = submitTotal ? newTotal : matchedRow.total;
                let stonetype = stoneMatchedRow.stonetype;

                if ((!(total === null || newExpPosition === null) && (newppc === 0 || minppc === null || newppc >= minppc)) || minppcChanged) {
                    data.push({
                        stoneid: matchedRow.stoneid,
                        newtotal: total,
                        newexpposition: newExpPosition,
                        actualposition: actualPosition,
                        minppc: minppcChanged ? matchedRow.minppc : minppc,
                        stonetype: stonetype
                    });
                }
                else {
                    invalidDataStoneIds.push(stoneid);
                }
            }
            else {
                invalidDataStoneIds.push(stoneid);
            }
            return true;
        })
        if (invalidDataStoneIds.length > 0) {
            setShowConfirmationModal(false);
            setSnackBarMessage("Stone id(s) " + invalidDataStoneIds.join(", ") + " is invalid or the values are unchanged. " +
                "Kindly uncheck the stones to proceed");
            setShowSnackbar(true);
            setSubmitLoader(false);
        }
        if (invalidDataStoneIds.length === 0) {
            NivodaProxy.updateStones(data, (response) => {
                if (response) {
                    setApplySubmitSuccessSnackbar(true);
                    setSubmitLoader(false);
                    setUploadResponse("success");
                    fetchLiveStoneData(searchCriteria, stockCriteriaState)
                    setSelectionModel([]);
                    setShowConfirmationModal(false);
                } else {
                    setApplySubmitSuccessSnackbar(true);
                    setSubmitLoader(false);
                    setUploadResponse("error");
                    setSelectionModel([]);
                    setShowConfirmationModal(false);
                }
            })
        }
    }

    const handleOnRefetchCriteria = () => {
        setRefetchLoader(true);
        let data = [];
        selectionModel.map((stoneid) => {
            const matchedRow = pricingList.find(data => String(data.stoneid) === String(stoneid))
            if (matchedRow) {
                data.push({
                    stoneid: matchedRow.stoneid,
                    filtertype: matchedRow.filtertype,
                    priority: 1,
                })
            }
            return true;
        })
        RefetchCriteria(data);
        setDisableFetchCriteria(true);
        setDisableSubmit(true);
    }

    const handleSelection = (data) => {
        const [tempStockData, tempSelectionModel] = Utility.handleSelection(data, pricingList, selectionModel);

        setPricingList(tempStockData);
        setSelectionModel(tempSelectionModel);
    }

    const handleStoneSelection = (data) => {
        let newSelectionModel = selectionModel;
        data.forEach(iStoneid => {
            newSelectionModel = newSelectionModel.filter(stone => stone !== iStoneid);
        });

        const [tempStockData, tempSelectionModel] = Utility.handleSelection(newSelectionModel, pricingList, newSelectionModel);

        setPricingList(tempStockData);
        setSelectionModel(tempSelectionModel);
    }

    const handleGetStoneData = (data) => {
        setLoadingButton(true);
        setSelectedIds(data);

        if (data.length > 0) {
            getPricingDataByIds(data, (fetchedStones) => {
                const uniqueValues = {
                    stonetype: new Set(),
                }
                const formattedFetchedStones = fetchedStones.map(stone => {
                    let formattedRecord = { ...stone };
                    formattedRecord.newtotal = 0.00.toFixed(2);
                    if (stone.priceList.length > 0) {
                        stone.priceList.forEach(priceItem => {
                            formattedRecord[`position${priceItem.position}`] = `${priceItem.price}(${priceItem.noOfStones})`;

                            if (formattedRecord.expposition > 0 && formattedRecord.expposition <= formattedRecord.priceList.length) {
                                formattedRecord.newtotal = ((formattedRecord.priceList[formattedRecord.expposition - 1].price) - 1).toFixed(2);
                            } else if (formattedRecord.expposition > formattedRecord.priceList.length) {
                                formattedRecord.newtotal = ((formattedRecord.priceList[formattedRecord.priceList.length - 1].price) + 1).toFixed(2);
                            }

                            const totalValue = parseFloat(formattedRecord.total);
                            const actualPositionIndex = stone.priceList.findIndex(priceItem => totalValue <= priceItem.price);
                            if (actualPositionIndex !== -1 && totalValue === (stone.priceList[actualPositionIndex].price).toFixed(2)) {
                                formattedRecord.actualposition = actualPositionIndex;
                            } else {
                                formattedRecord.actualposition = (actualPositionIndex === -1) ? stone.priceList.length + 1 : actualPositionIndex + 1;
                            }
                        });
                    }

                    Object.keys(uniqueValues).forEach(key => {
                        if (stone[key] !== null && stone[key] !== "") {
                            uniqueValues[key].add(stone[key]);
                        }
                    });

                    return formattedRecord;
                });
                const newPricingList = [...formattedFetchedStones];
                setStoneType([...uniqueValues.stonetype]);
                setPricingList(newPricingList);
                setLoadingButton(false);
                setShowGetStoneDataModal(false);
            });
        }
    }

    const handleStoneDetailModal = (params) => {
        setStoneId(params.value);
        setShowStoneDetailModal(true);
    };

    const handleProcessRowUpdateError = (error) => {
        // console.error(error);
    };

    const handleColumnOrderChange = (params) => {
        const { targetIndex, oldIndex } = params;

        const newColumns = [...columns];
        const [movedColumn] = newColumns.splice(oldIndex - 1, 1);
        newColumns.splice(targetIndex - 1, 0, movedColumn);
        setColumns(newColumns);

        let columnOrder = [];
        newColumns.map(col => columnOrder.push(col.field))
        localStorage.setItem('colOrder', JSON.stringify(columnOrder));
    };

    const customFilterOperators = [
        {
            label: '=',
            value: 'equals',
            getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) return null;
                return ({ value }) => value === Number(filterItem.value);
            },
            InputComponent: GridFilterInputValue,
        },
        {
            label: '!=',
            value: 'notEquals',
            getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) return null;
                return ({ value }) => value !== Number(filterItem.value);
            },
            InputComponent: GridFilterInputValue,
        },
        {
            label: '>=',
            value: 'greaterThanOrEqual',
            getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) return null;
                return ({ value }) => value >= Number(filterItem.value);
            },
            InputComponent: GridFilterInputValue,
        },
        {
            label: '>',
            value: 'greaterThan',
            getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) return null;
                return ({ value }) => value > Number(filterItem.value);
            },
            InputComponent: GridFilterInputValue,
        },
        {
            label: '<=',
            value: 'lessThanOrEqual',
            getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) return null;
                return ({ value }) => value <= Number(filterItem.value);
            },
            InputComponent: GridFilterInputValue,
        },
        {
            label: '<',
            value: 'lessThan',
            getApplyFilterFn: (filterItem) => {
                if (!filterItem.value) return null;
                return ({ value }) => value < Number(filterItem.value);
            },
            InputComponent: GridFilterInputValue,
        },
    ];

    const hasColorFilterOperator = {
        label: 'has color',
        value: 'hasColor',
        getApplyFilterFn: (filterItem, column) => {
            if (!filterItem.value) {
                return null;
            }
            //keep this
            // return (params) => {
            //     const cellClassName = params.row[params.field];
            //     console.log(cellClassName);
            //     // return cellClassName.includes(filterItem.value);
            // };
            return ({ value, row, column, apiRef }) => {
                const { actualposition, expposition, total, newtotal } = row;
                let cellClassName = '';
                if (actualposition === null || expposition === null || actualposition === '' || expposition === '' || actualposition === undefined || expposition === undefined) {
                    cellClassName = '';
                }
                else if (actualposition < expposition) cellClassName = 'greenCell';
                else if (actualposition > expposition) cellClassName = 'redCell';
                else if (actualposition === expposition) {
                    cellClassName = Number(total) < Number(newtotal) ? 'yellowCell' : '';
                }
                return cellClassName.startsWith(filterItem.value.toLowerCase());
            };
        },
        InputComponent: ({ item, applyValue }) => (
            <FormControl fullWidth sx={{ marginLeft: 1 }}>
                <InputLabel>Value</InputLabel>
                <Select
                    value={item.value || ''}
                    onChange={(event) => {
                        const newValue = event.target.value || null;
                        applyValue({ ...item, value: newValue });
                    }}
                    label="Value"
                    sx={{ height: "49px", width: '11rem' }}
                >
                    <MenuItem value=""><em>None</em></MenuItem>
                    <MenuItem value="green">Green</MenuItem>
                    <MenuItem value="red">Red</MenuItem>
                    <MenuItem value="yellow">Yellow</MenuItem>
                </Select>
            </FormControl>
        ),
        InputComponentProps: {
            type: 'singleSelect',
            valueOptions: ['green', 'red', 'yellow'],
        },
    };

    const defaultFilters = () => {
        const stringOperators = getGridStringOperators().filter(operator =>
            ['isEmpty', 'isNotEmpty', 'isAnyOf'].includes(operator.value)
        );
        const combinedFilterOperators = [hasColorFilterOperator, ...customFilterOperators, ...stringOperators];
        return combinedFilterOperators;
    }

    const combinedFilters = () => {
        return [
            ...getGridSingleSelectOperators(),
            ...getGridStringOperators().filter(operator => operator.value !== 'isAnyOf'),
        ]
    }

    const handleFilterChange = (model) => {
        setFilterModel(model);
    };


    const handleClear = () => {
        const fileInput = document.getElementById('file-input');
        fileInput.value = null;
    }

    const handleUpload = (file) => {
        const reader = new FileReader();
        setUploadLoader(true);
        reader.onload = () => {
            const text = reader.result;
            Papa.parse(text, {
                header: true,
                skipEmptyLines: true,
                complete: (result) => {
                    if (result.data.length <= 0) {
                        setAlertSnackbar(true);
                        setAlertMessage("No Stones Found");
                        return;
                    }
                    let headersFromCSV = Object.keys(result.data[0]);
                    let requiredColumns = ['stoneid', 'minppc', 'newexppos'];
                    if (headersFromCSV.length === requiredColumns.length) {
                        const newData = result.data;

                        setPricingList((prevPricingList) => {
                            const updatedPricingList = prevPricingList.map(item => {
                                const foundItem = newData.find(data => data.stoneid === item.stoneid);
                                if (foundItem) {
                                    // Only update fields that have values in the CSV
                                    if (foundItem.minppc !== '') item.minppc = foundItem.minppc;
                                    if (foundItem.newexppos !== '') item.newexpposition = foundItem.newexppos;
                                }
                                return item;
                            });
                            return updatedPricingList;
                        });

                        const stoneIds = newData.map(data => data.stoneid);

                        setSelectionModel(stoneIds);
                        setUploadLoader(false);
                        setUploadSuccessSnackbar(true);
                        setDisableSubmit(false);
                        setDisableFetchCriteria(false);
                    } else {
                        console.error('CSV columns do not match table columns.');
                        setUploadSuccessSnackbar(true);
                        setUploadLoader(false);
                    }
                },
                error: (error) => {
                    console.error('Error parsing CSV:', error);
                    setAlertSnackbar(true)
                }
            });
        };
        reader.readAsText(file);
    };

    const operatorFunctions = {
        '=': (rowValue, filterValue) => rowValue === filterValue,
        '!=': (rowValue, filterValue) => rowValue !== filterValue,
        '>': (rowValue, filterValue) => rowValue > filterValue,
        '<': (rowValue, filterValue) => rowValue < filterValue,
        '>=': (rowValue, filterValue) => rowValue >= filterValue,
        '<=': (rowValue, filterValue) => rowValue <= filterValue,
        'contains': (rowValue, filterValue) => rowValue.includes(filterValue),
        'startsWith': (rowValue, filterValue) => rowValue.startsWith(filterValue),
        'endsWith': (rowValue, filterValue) => rowValue.endsWith(filterValue),
        'isEmpty': (rowValue) => rowValue === '',
        'isNotEmpty': (rowValue) => rowValue !== '',
        'is': (rowValue, filterValue) => rowValue === filterValue,
        'isNot': (rowValue, filterValue) => rowValue !== filterValue,
        'isAnyOf': (rowValue, filterValues) => filterValues.includes(rowValue), // Multiselect logic
        'equals': (rowValue, filterValue) => rowValue === filterValue,
        'hasColor': (row, filterValue) => {
            if (filterValue) {
                const { actualposition, expposition, total, newtotal } = row;
                let cellClassName = '';
                if (actualposition === null || expposition === null || actualposition === '' || expposition === '' || actualposition === undefined || expposition === undefined) {
                    cellClassName = '';
                } else if (actualposition < expposition) cellClassName = 'greenCell';
                else if (actualposition > expposition) cellClassName = 'redCell';
                else if (actualposition === expposition) {
                    cellClassName = Number(total) < Number(newtotal) ? 'yellowCell' : '';
                }
                return cellClassName.startsWith(filterValue.toLowerCase());
            }
        },
    };

    const applyFilters = (filterModel) => {
        const rowMatchesFilters = (row) => {
            return filterModel.items.every(({ field, operator, value }) => {
                const operatorFunction = operatorFunctions[operator];
                const rowValue = (row[field] !== null && row[field] !== undefined) ? row[field].toString() : '';
                return operatorFunction ? operatorFunction(rowValue, value) : true;
            });
        };

        const filteredRows = pricingList.filter(row => rowMatchesFilters(row));
        setFilteredPricingList(filteredRows);
    };


    const applyValueToAllFields = () => {
        setApplyLoader(true);
        // Clone pricingList to avoid mutating the original list directly
        const updatedPricingList = pricingList.map(row => ({ ...row }));

        let rowsToUpdate = [];
        if (filteredPricingList.length > 0 && selectionModel.length > 0) {
            // Apply to selected rows within the filtered list
            rowsToUpdate = filteredPricingList.filter(row => selectionModel.includes(row.stoneid));
        } else if (selectionModel.length > 0) {
            // Apply to selected rows if no filters are applied
            rowsToUpdate = updatedPricingList.filter(row => selectionModel.includes(row.stoneid));
        } else if (filteredPricingList.length > 0) {
            // Apply to filtered rows if no selection exists
            rowsToUpdate = filteredPricingList;
        }
        rowsToUpdate.forEach(filteredRow => {
            const matchingRowIndex = updatedPricingList.findIndex(row => row.stoneid === filteredRow.stoneid);

            if (matchingRowIndex !== -1) {
                let updatedRow = { ...updatedPricingList[matchingRowIndex] };

                if (applyToAll === "newexpposition" && parseInt(applyValue) > 0) {
                    if (parseInt(applyValue) !== parseInt(filteredRow.newexpposition)) {
                        updatedRow.newexpposition = Number(applyValue);

                        const newexpposition = parseInt(applyValue);
                        let newTotal = filteredRow.newtotal;

                        if (filteredRow.priceList && filteredRow.priceList.length > 0) {
                            if (newexpposition > 0 && newexpposition <= filteredRow.priceList.length) {
                                newTotal = (filteredRow.priceList[newexpposition - 1].price) - 1;
                            } else if (newexpposition > filteredRow.priceList.length) {
                                newTotal = (filteredRow.priceList[filteredRow.priceList.length - 1].price) + 1;
                            }
                            updatedRow.newtotal = newTotal.toFixed(2);
                            updatedRow.percentchange = (((updatedRow.newtotal * 100) / (updatedRow.total)) - 100).toFixed(2);
                        }
                    }
                } else if (applyToAll === "newtotal" && parseFloat(applyValue) > 0) {
                    updatedRow.newtotal = parseFloat(applyValue).toFixed(2);

                    const newTotal = parseFloat(applyValue);
                    if (filteredRow.priceList && filteredRow.priceList.length > 0) {
                        let closestPosition = null;

                        for (const priceItem of filteredRow.priceList) {
                            const price = parseFloat(priceItem.price);
                            if (newTotal < price) {
                                closestPosition = priceItem.position;
                                break;
                            } else {
                                closestPosition = priceItem.position + 1;
                            }
                        }
                        updatedRow.newexpposition = closestPosition;
                        updatedRow.percentchange = (((updatedRow.newtotal * 100) / (updatedRow.total)) - 100).toFixed(2);
                    }
                } else if (applyToAll === "minppc" && parseFloat(applyValue) !== parseFloat(filteredRow.minppc)) {
                    updatedRow.minppc = parseFloat(applyValue).toFixed(2);
                }

                updatedPricingList[matchingRowIndex] = updatedRow;
            }
        });
        setPricingList(updatedPricingList);
        setApplyLoader(false);
    };

    const initialColumns = (stoneList) => [
        {
            field: "id",
            headerName: "Id",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 80,
            renderCell: renderCellExpand,
        },
        {
            field: "stoneid",
            headerName: "STONEID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            width: 80,
            renderCell: (params) => (Utility.NeedToShowStoneDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: 'black'
                    }}
                    onClick={() => handleStoneDetailModal(params)}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            cellClassName: (params) => {

                const stoneId = stoneList.filter(item => item.stoneid.toString() === params.value.toString());
                return !stoneId.length > 0 ? 'greenCell' : '';

            }
        },
        {
            field: "kapanid",
            headerName: "KAPANID",
            headerAlign: "center",
            align: "center",
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (Utility.NeedToShowKapanDetailModal()) ?
                <Button
                    sx={{
                        textDecorationLine: "underline",
                        color: "black"
                    }}
                    onClick={() => {
                        setKapanId(params.row.kapanid);
                        setShowKapanDetailModal(true);
                    }}
                >
                    {params.value}
                </Button>
                :
                <div>{params.value}</div>,
            width: 80,
        },
        {
            field: "packetid",
            headerName: "PACKETID",
            headerAlign: "center",
            align: "center",
            valueFormatter: (params) => params.value,
            width: 80,
            renderCell: (params) => {
                if (params.value === null || params.row.kapanid === null) {
                    return;
                } else {
                    return Utility.NeedToShowPacketDetailModal() ? (
                        <Button
                            sx={{ textDecorationLine: "underline", color: "black" }}
                            onClick={() => {
                                setPacketId(params.row.packetid);
                                setKapanId(params.row.kapanid);
                                setShowPacketDetailModal(true);
                            }}
                        >
                            {params.value}
                        </Button>
                    ) : (
                        <div>{params.value}</div>
                    );
                }
            }
        },
        {
            field: "mumid",
            headerName: "ID",
            headerAlign: "center",
            align: "center",
            width: 90,
            renderCell: (params) => (!params.value) ? "" : params.value,
        },
        {
            field: "suratid",
            headerName: "REF NO",
            headerAlign: "center",
            align: "center",
            width: 90,
            renderCell: (params) => (!params.value) ? "" : params.value,
        },
        {
            field: "status",
            headerName: "STATUS",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "singleSelect",
            valueOptions: ["AVAILABLE", "HOLD", "MEMO_OUT"],
            filterOperators: combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "lab",
            headerName: "LAB",
            headerAlign: "center",
            align: "center",
            width: 1,
            type: "singleSelect",
            valueOptions: lab,
            filterOperators: combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "size",
            headerName: "SIZE",
            headerAlign: "center",
            align: "center",
            width: 30,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => {
                const value = String(params.value);
                if (value && value.includes('-')) {
                    return value;
                } else {
                    return Number(value).toFixed(Constants.DEFAULT_NO_OF_DIGITS);
                }
            },
        },
        {
            field: "shape",
            headerName: "SHAPE",
            headerAlign: "center",
            align: "center",
            width: 65,
            type: "singleSelect",
            valueOptions: shapes,
            filterOperators: combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "color",
            headerName: "COL",
            headerAlign: "center",
            align: "center",
            width: 10,
            type: "singleSelect",
            valueOptions: colors,
            filterOperators: combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "clarity",
            headerName: "CLR",
            headerAlign: "center",
            align: "center",
            width: 55,
            type: "singleSelect",
            valueOptions: clarity,
            filterOperators: combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "cut",
            headerName: "CUT",
            headerAlign: "center",
            align: "center",
            width: 30,
            type: "singleSelect",
            valueOptions: cut,
            filterOperators: combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "pol",
            headerName: "POL",
            headerAlign: "center",
            align: "center",
            width: 30,
            type: "singleSelect",
            valueOptions: pol,
            filterOperators: combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "sym",
            headerName: "SYM",
            headerAlign: "center",
            align: "center",
            width: 30,
            type: "singleSelect",
            valueOptions: sym,
            filterOperators: combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "flo",
            headerName: "FLO",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: flo,
            filterOperators: combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "tinge",
            headerName: "TINGE",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: tinge,
            filterOperators: combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "milky",
            headerName: "MILKY",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "singleSelect",
            valueOptions: milky,
            filterOperators: combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "age",
            headerName: "AGE",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "number",
            valueFormatter: (params) => params.value,
            valueGetter: (params) => {
                if (params.value)
                    return Utility.ConvertDateToAge(params.value);
                else
                    return null;
            },
            renderCell: (params) => {
                return params.value;
            },
        },
        {
            field: "lastrevise",
            headerName: "LASTREVISE",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "number",
            valueFormatter: (params) => params.value,
            valueGetter: (params) => {
                if (params.value)
                    return Utility.ConvertDateToAge(params.value);
                else
                    return null;
            },
            renderCell: (params) => {
                return params.value;
            },
        },
        {
            field: "position",
            headerName: "CUR POS",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "prevposition",
            headerName: "PREV POS",
            headerAlign: "center",
            align: "center",
            width: 90,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "lastpositionrevisedate",
            headerName: "LST REV POS DT",
            headerAlign: "center",
            align: "center",
            width: 120,
            type: "number",
            valueFormatter: (params) => params.value,
            valueGetter: (params) => {
                if (params.value)
                    return Utility.ConvertDateToAge(params.value);
                else
                    return null;
            },
            renderCell: (params) => {
                return params.value;
            },
        },
        {
            field: "discount",
            headerName: "DIS",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Number(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
        },
        {
            field: "price",
            headerName: "PPC",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Number(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
        },
        {
            field: "total",
            headerName: "TOTAL",
            headerAlign: "center",
            align: "center",
            width: 60,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Number(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
        },
        {
            field: "minppc",
            headerName: "MINPPC",
            headerAlign: "center",
            align: "center",
            width: 80,
            editable: true,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>
                        {params.value !== undefined && params.value !== null
                            ? Number(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
                            : '0.00'}
                    </span>
                    {params.colDef.editable && <EditIcon style={{ marginLeft: 3, marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell',
        },
        {
            field: "newtotal",
            headerName: "NEWTOTAL",
            headerAlign: "center",
            align: "center",
            width: 85,
            editable: true,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>{params.value}</span>
                    {params.colDef.editable && <EditIcon style={{ marginLeft: 5, marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell',
        },
        {
            field: "percentchange",
            headerName: "% CHANGE",
            headerAlign: "center",
            align: "center",
            width: 85,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div className={useStyles()[params.cellClassName]}>
                    {params.value}
                </div>
            ),
            cellClassName: (params) => {
                if (params.value === null || params.value === '' || params.value === undefined)
                    return '';
                else if (params.value > 0) return 'greenCell';
                else if (params.value < 0) return 'redCell';
                else return '';
            },
        },
        {
            field: "actualposition",
            headerName: "ACT POS",
            headerAlign: "center",
            align: "center",
            width: 80,
            renderCell: (params) => (
                <div className={useStyles()[params.cellClassName]}>
                    {params.value}
                </div>
            ),
            cellClassName: (params) => {
                const { actualposition, expposition, total, newtotal } = params.row;
                if (actualposition === null || expposition === null || actualposition === '' || expposition === '' || actualposition === undefined || expposition === undefined) {
                    return '';
                }
                else if (actualposition < expposition) return 'greenCell';
                else if (actualposition > expposition) return 'redCell';
                else if (actualposition === expposition) {
                    return Number(total) < Number(newtotal) ? 'yellowCell' : '';
                }
                return '';
            },
            filterOperators: defaultFilters()
        },
        {
            field: "expposition",
            headerName: "EXP POS",
            headerAlign: "center",
            align: "center",
            width: 80,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "newexpposition",
            headerName: "NEW EXP POS",
            headerAlign: "center",
            align: "center",
            width: 80,
            hideable: false,
            editable: true,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span style={{ marginRight: '5px', marginLeft: '10px' }}>{params.value}</span>
                    {params.colDef.editable && <EditIcon style={{ marginLeft: 30, marginBottom: 3, fontSize: '1rem' }} />}
                </div>
            ),
            cellClassName: (params) => 'lavenderCell',
        },

        ...Array.from({ length: 11 }, (_, index) => ({
            field: `position${index + 1}`,
            headerName: `${index + 1}`,
            headerAlign: "center",
            align: "center",
            width: 85,
            renderCell: renderCellExpand,
        })),
        {
            field: "rapnetprice",
            headerName: "RAP",
            headerAlign: "center",
            align: "center",
            width: 55,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: renderCellExpand,
        },
        {
            field: "iteration",
            headerName: "DT FCH",
            headerAlign: "center",
            align: "center",
            width: 70,
            type: "date",
            valueGetter: (params) => {
                if (params.value) {
                    return new Date(params.value);
                }
                return null;
            },
            renderCell: (params) => {
                if (params.value) {
                    return Utility.ConvertUtcToLocalDate(params.value);
                } else {
                    return "";
                }
            }
        },
        {
            field: "stonetype",
            headerName: "STONETYPE",
            headerAlign: "center",
            align: "center",
            width: 150,
            type: "singleSelect",
            valueOptions: stonetype,
            filterOperators: combinedFilters(),
            renderCell: renderCellExpand
        },
        {
            field: "location",
            headerName: "LOC",
            headerAlign: "center",
            align: "center",
            width: 85,
            type: "singleSelect",
            valueOptions: location,
            filterOperators: combinedFilters(),
            renderCell: renderCellExpand,
        },
        {
            field: "measurements",
            headerName: "MEASUREMENTS",
            headerAlign: "center",
            align: "center",
            width: 130,
            renderCell: renderCellExpand,
        },
        {
            field: "depth",
            headerName: "DEP",
            headerAlign: "center",
            align: "center",
            width: 40,
            type: "number",
            valueFormatter: (params) => params.value,
            renderCell: (params) => Number(params.value).toFixed(Constants.DEFAULT_NO_OF_DIGITS)
        },
        {
            field: "certificateid",
            headerName: "CERT",
            headerAlign: "center",
            align: "center",
            width: 90,
            renderCell: renderCellExpand,
        },
    ];

    const columnName = columns.filter(iColumn => {
        if (iColumn.field === "mumid" && !mumidPermission) return false;
        if (iColumn.field === "suratid" && !suratidPermission) return false;
        return true;
    });

    return (
        <Box>
            <CommonSnackbar
                show={errorSnackbar}
                message={Constants.APiFailErrorMessage}
                variant={"error"}
                autoHide={Constants.SnackBarCloseTimer}
                title={"Failed"}
                handleClose={() => setErrorSnackbar(false)}
            />
            <CommonSnackbar
                show={alertSnackbar}
                message={alertMessage}
                variant={"error"}
                autoHide={false}
                title={"Error"}
                handleClose={handleClose}
            />
            {showSnackbar ? (
                <CommonSnackbar
                    show={showSnackbar}
                    message={
                        snackBarMessage
                    }
                    variant={"warning"}
                    autoHide={false}
                    title={"Warning"}
                    handleClose={() => {
                        setShowSnackbar(false);
                    }}
                />)
                : null}
            {pricingList ? (
                <CommonSnackbar
                    show={uploadSuccessSnackbar}
                    message={
                        pricingList.length !== 0
                            ? (selectionModel.length === 0 ? `Please correct your data and retry again` : `File loaded Successfully`)
                            : `File could not be loaded. Please correct your data and retry again`
                    }
                    variant={pricingList.length !== 0 && selectionModel.length > 0 ? "success" : "error"}
                    autoHide={true}
                    title={pricingList.length !== 0 && selectionModel.length > 0 ? "Success" : "Failed"}
                    handleClose={handleClose}
                />
            ) : null}
            {ApplyRefetchSuccessSnackbar && uploadResponse !== undefined && uploadResponse !== "" ? (
                <CommonSnackbar
                    show={ApplyRefetchSuccessSnackbar}
                    message={
                        uploadResponse === "success"
                            ? `Criteria Refetched Successfully`
                            : `Criteria Could Not Be Refetched`
                    }
                    variant={uploadResponse === "success" ? "success" : "error"}
                    autoHide={true}
                    title={uploadResponse === "success" ? "success" : "Error"}
                    handleClose={handleClose}
                />
            ) : null}
            {ApplySuccessSnackbar && uploadResponse !== undefined && uploadResponse !== "" ? (
                <CommonSnackbar
                    show={ApplySuccessSnackbar}
                    message={
                        uploadResponse === "success"
                            ? `Stone Position Applied Successfully`
                            : `Stone Position Could Not Be Applied`
                    }
                    variant={uploadResponse === "success" ? "success" : "error"}
                    autoHide={true}
                    title={uploadResponse === "success" ? "success" : "Error"}
                    handleClose={handleClose}
                />
            ) : null}
            {ApplySubmitSuccessSnackbar && uploadResponse !== undefined && uploadResponse !== "" ? (
                <CommonSnackbar
                    show={ApplySubmitSuccessSnackbar}
                    message={
                        uploadResponse === "success"
                            ? `Stone updated Successfully`
                            : `Stone Could Not Be Updated. Enter correct data and try again.`
                    }
                    variant={uploadResponse === "success" ? "success" : "error"}
                    autoHide={true}
                    title={uploadResponse === "success" ? "success" : "Error"}
                    handleClose={handleClose}
                />
            ) : null}
            <Drawer
                anchor="left"
                open={drawerExpanded}
                style={{ width: "50%" }}
                onClose={() => setDrawerExpanded(!drawerExpanded)}>
                <StockSearchCriteria
                    displaySearchCriteria={false}
                    stockCriteriaStored={stockCriteriaState}
                    handleSearch={fetchLiveStoneData}
                    showSaveSearchAs={false}
                    setCustomerId={(data) => { }}
                    drawerClose={() => setDrawerExpanded(false)}
                    setFilters={(stockCriteriaStored) => setStockCriteriaState(stockCriteriaStored)}
                    readOnly={false}
                    enableReportNoSearchBox={false}
                />
            </Drawer>

            {showStoneDetailModal ? (
                <StoneDetailModal
                    show={showStoneDetailModal}
                    stoneId={stoneId}
                    handleClose={handleClose}
                />
            ) : null}

            {showConfirmationModal ? (
                <ConfirmationModal
                    show={showConfirmationModal}
                    setShow={setShowConfirmationModal}
                    handleSave={handleSubmit}
                    modalTitle={confirmModalTitle}
                    modalMessage={confirmModalMessage}
                    loading={submitLoader}
                />
            ) : null}

            {showKapanDetailModal ? (
                <KapanHistoryModal
                    show={showKapanDetailModal}
                    kapanId={kapanId}
                    close={() => setShowKapanDetailModal(false)}
                />
            ) : null}

            {showPacketDetailModal ? (
                <PacketHistoryModal
                    show={showPacketDetailModal}
                    packetId={packetId}
                    kapanId={kapanId}
                    close={() => setShowPacketDetailModal(false)}
                />
            ) : null}

            {showStoneSelectionModal ? (
                <StoneSelectionModal
                    show={showStoneSelectionModal}
                    handleSave={handleStoneSelection}
                    handleClose={() => setShowStoneSelectionModal(false)}
                    title={"Remove stones from selection"}
                />
            ) : null}

            {
                access ? <NoPermission /> : (
                    <Box px={1}>

                        <Box m={"12px 5px 12px 0px"}
                            sx={{ display: "flex", gap: "15px", justifyContent: "space-between" }}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    gap: "20px",

                                }}
                            >
                                <Tooltip title="Show All Filters">
                                    <Button
                                        variant="contained"
                                        size="small"
                                        color='inherit'
                                        sx={{ ml: "5px", backgroundColor: '#454545', width: "128px", height: "50px", color: 'white', '&:hover': { backgroundColor: '#454545' } }}
                                        startIcon={<TuneIcon size="large" />}
                                        onClick={() => setDrawerExpanded(true)}
                                    >
                                        All Filters
                                    </Button>
                                </Tooltip>

                                <Button
                                    variant="outlined"
                                    size="small"
                                    sx={{ ml: "5px", backgroundColor: '#454545', width: "160px", height: "50px", color: 'white', '&:hover': { backgroundColor: '#454545' } }}
                                    onClick={() => setShowGetStoneDataModal(true)}
                                    startIcon={<AddTaskIcon size="large" />}
                                    disabled={loadingButton}
                                >
                                    Get Stone Data
                                </Button>
                            </Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    "& .MuiOutlinedInput-root": {
                                        height: "2.25rem",
                                        width: "170px"
                                    },
                                    "& .MuiTextField-root": {
                                        marginTop: 0,
                                    }
                                }}
                                gap="15px">

                                <Box
                                    sx={{
                                        "& .MuiInputLabel-root": {
                                            marginBottom: 0,
                                            lineHeight: "17px"
                                        }
                                    }}
                                >
                                    <TextField
                                        fullWidth
                                        label="Total / New Exp Pos"
                                        value={applyValue}
                                        sx={{
                                            width: "160px",
                                            "& .MuiInputBase-input": {
                                                padding: "6px 12px 7px",
                                            },
                                        }}
                                        onChange={(e) => setApplyValue(e.target.value)}
                                    />
                                </Box>

                                <FormControl sx={{ width: "160px" }}>
                                    <InputLabel>Option</InputLabel>
                                    <Select
                                        sx={{
                                            '& .MuiOutlinedInput-input': {
                                                padding: "6px 12px 7px",
                                                lineHeight: "1.5"
                                            },
                                        }}
                                        labelId="select-label"
                                        value={applyToAll}
                                        onChange={(event) => {
                                            setApplyToAll(event.target.value);
                                        }}
                                        label="Value"
                                    >
                                        <MenuItem value="newexpposition">New Exp Pos</MenuItem>
                                        <MenuItem value="newtotal">New Total</MenuItem>
                                        <MenuItem value="minppc">Min PPC</MenuItem>
                                    </Select>
                                </FormControl>

                                <LoadingButton
                                    variant="contained"
                                    onClick={applyValueToAllFields}
                                    type="Submit"
                                    color="success"
                                    disabled={disableApply || disableAllButtons}
                                    loading={applyLoader}
                                    loadingPosition="start"
                                    startIcon={<CheckCircleIcon />}
                                    sx={{ width: "105px", height: "2.25rem" }}
                                >
                                    Apply
                                </LoadingButton>

                                <Box display={"flex"} alignItems={"center"}>
                                    <Typography m={0} mr={"10px"}>Date</Typography>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            className="picker"
                                            ampm={true}
                                            renderInput={(props) => (
                                                <TextField
                                                    fullWidth
                                                    {...props}
                                                    inputProps={{ ...props.inputProps, readOnly: true }}
                                                />
                                            )}
                                            error={false}
                                            value={toDate}
                                            inputFormat="DD/MM/YYYY"
                                            onChange={(newValue) => {
                                                // Get the current time
                                                const now = dayjs();

                                                // Set startOfMonth to the first day of the selected month, but with the current system time
                                                const toDate = dayjs(newValue)
                                                    .hour(now.hour())
                                                    .minute(now.minute())
                                                    .second(now.second())
                                                    .millisecond(now.millisecond())
                                                    .toDate();

                                                setToDate(toDate);
                                            }}
                                        />
                                    </LocalizationProvider>
                                </Box>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                backgroundColor: '#fff',
                                color: '#000',
                                width: "100%",
                                position: "relative",
                                "& .MuiTablePagination-selectLabel": {
                                    marginTop: "15px",
                                },
                                "& .MuiTablePagination-displayedRows": {
                                    marginTop: "15px",
                                },
                                "& .css-1jbbcbn-MuiDataGrid-columnHeaderTitle": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .css-cc8tf1": {
                                    fontWeight: Constants.defaultFontWeight,
                                },
                                "& .lavenderCell": {
                                    color: "#080808",
                                    backgroundColor: "#D8BFD8",
                                },
                                "& .greenCell": {
                                    color: "#080808",
                                    backgroundColor: "#B6D7A8"
                                },
                                "& .redCell": {
                                    color: "#080808",
                                    backgroundColor: "#f5d7d7",
                                },
                                "& .yellowCell": {
                                    color: "#080808",
                                    backgroundColor: "#F1E68C",
                                },
                                "& .MuiDataGrid-toolbarContainer": {
                                    padding: "4px 4px 0 90px !important"
                                },
                                height: "76vh",
                            }}
                        >
                            <StyledDataGridPro
                                disableRowSelectionOnClick={false}
                                density="compact"
                                loading={pricingListLoader}
                                sx={{
                                    '& .MuiDataGrid-columnHeader': {
                                        color: '#fff',
                                        backgroundColor: 'grey',
                                    },
                                    "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": {
                                        py: "4px",
                                    },
                                    "&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell": {
                                        py: "12px",
                                    },
                                    "&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell": {
                                        py: "18px",
                                    },

                                    fontSize: Constants.defaultTableFontSize, fontWeight: Constants.defaultFontWeight,
                                }}
                                slots={{ toolbar: GridToolbar }}
                                checkboxSelection={true}
                                rowSelectionModel={selectionModel}
                                onRowSelectionModelChange={(newSelectionModel) => {
                                    setSelectionModel(newSelectionModel);
                                }}
                                selectionModel={selectionModel}
                                processRowUpdate={processRowUpdate}
                                onProcessRowUpdateError={handleProcessRowUpdateError}
                                onColumnOrderChange={handleColumnOrderChange}
                                rows={pricingList}
                                columns={columnName}
                                getRowId={(row) => row.stoneid}
                                rowSpacingType="border"
                                filterModel={filterModel}
                                onFilterModelChange={(newFilterModel) => {
                                    handleFilterChange(newFilterModel);
                                    applyFilters(newFilterModel);
                                }}
                                initialState={{
                                    columns: {
                                        columnVisibilityModel: {
                                            // Hide columns status and traderName, the other columns will remain visible
                                            id: false,
                                        },
                                    },
                                    pagination: {
                                        paginationModel: Constants.pageSize,
                                    },
                                }}
                                pageSizeOptions={Constants.pageSizeOptions}
                                pagination
                                slotProps={{
                                    toolbar: {
                                        showQuickFilter: true,
                                        quickFilterProps: { debounceMs: 500 },
                                    },
                                }}
                                apiRef={apiRef}
                                onCellClick={handleCellClick}
                                editMode="cell"
                            />

                            <Box sx={{
                                position: "absolute",
                                left: "0.4rem",
                                top: "1px",
                                display: "flex",
                            }}>
                                <CommonStoneSelection title={"Enter multiple Stone Ids"} handleSelection={handleSelection} buttonDisabled={pricingList.length === 0} />
                                <Tooltip title={"Remove multiple stone ids"}>
                                    <IconButton
                                        color={"inherit"}
                                        onClick={() => setShowStoneSelectionModal(true)}
                                        disabled={pricingList.length === 0}
                                        sx={{
                                            color: '#454545',
                                            borderColor: '#454545'
                                        }}
                                    >
                                        <IndeterminateCheckBoxIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Box sx={{
                                position: "absolute",
                                left: "505px",
                                top: "3px",
                                display: "flex",
                                alignItems: "center",
                                "& .MuiFormControlLabel-root": {
                                    margin: 0
                                }
                            }}>
                                <Typography
                                    sx={{
                                        fontSize: "16px",
                                        fontWeight: "600",
                                        margin: 0,
                                        color: "black",
                                        paddingRight: "12px",
                                        paddingTop: "8px"
                                    }}
                                > Save New Total</Typography>

                                <FormControlLabel
                                    control={<NewStoneSwitch />}
                                    onChange={() => setSubmitTotal(!submitTotal)}
                                    checked={submitTotal}
                                    disabled={disableAllButtons}
                                />
                            </Box>

                            <Box
                                style={{
                                    position: "absolute",
                                    top: 5,
                                    right: "270px",
                                    display: "flex",
                                    flexWrap: "nowrap",
                                    height: "33px",
                                    alignItems: "center"
                                }}>
                                {searchCriteria === "" ? (
                                    <Box />
                                ) : pricingListLoader ? (
                                    <CircularProgress size="1rem" />
                                ) : (
                                    <Tooltip title="Reload List">
                                        <RefreshOutlinedIcon
                                            onClick={() => fetchLiveStoneData(searchCriteria, stockCriteriaState)}
                                            color="primary"
                                            fontSize="medium"
                                            sx={{ cursor: "pointer" }}
                                        />
                                    </Tooltip>
                                )}
                                <Tooltip title="Download Sample Min PPC Upload">
                                    <a
                                        href="/demo_excel/SamplePricingMinPpcUpload.csv"
                                        download="SamplePricingMinPpcUpload.csv"
                                        style={{ color: "red", marginRight: "10px", marginLeft: "10px" }}
                                    >
                                        <BackupTable size={25} />
                                    </a>
                                </Tooltip>

                                <Tooltip title="Upload Min PPC/New Exp Pos">
                                    <LoadingButton
                                        color="info"
                                        variant="outlined"
                                        size="small"
                                        sx={{ mr: "10px" }}
                                        component="label"
                                        loading={uploadLoader}
                                        loadingPosition="start"
                                        startIcon={<UploadFileIcon size="large" />}
                                    >
                                        Upload Min PPC/New Exp Pos
                                        <input
                                            id="file-input"
                                            hidden
                                            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                                            multiple
                                            type="file"
                                            onChange={(e) => handleUpload(e.target.files[0])}
                                            onClick={(e) => handleClear()}
                                        />
                                    </LoadingButton>
                                </Tooltip>
                                <Tooltip title="Refetch Criteria">
                                    <LoadingButton
                                        variant="outlined"
                                        size="small"
                                        component="label"
                                        sx={{ ml: "5px", mr: "5px" }}
                                        disabled={disableFetchCriteria || disableAllButtons}
                                        onClick={handleOnRefetchCriteria}
                                        loading={refetchLoader}
                                        loadingPosition="start"
                                        startIcon={<AddBoxOutlinedIcon size="large" />}
                                    >
                                        Refetch Criteria
                                    </LoadingButton>
                                </Tooltip>
                                <Tooltip title="Submit Stones">
                                    <LoadingButton
                                        variant="outlined"
                                        size="small"
                                        component="label"
                                        sx={{ ml: "5px" }}
                                        loading={submitLoader}
                                        loadingPosition="start"
                                        startIcon={<ThumbUpAltIcon size="large" />}
                                        disabled={disableSubmit || disableAllButtons}
                                        onClick={handleIfSubmit}
                                    >
                                        Submit
                                    </LoadingButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    </Box>
                )
            }

            {showGetStoneDataModal ? (
                <Modal
                    show={showGetStoneDataModal}
                    onHide={() => setShowGetStoneDataModal(false)}
                    backdrop="static"
                    keyboard={true}
                    size="lg"
                    centered
                    enforceFocus={false}
                >
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <img
                                src={logo}
                                height="40pt"
                                width="40pt"
                                alt={Constants.COMPANY_SITE}
                            />
                            &nbsp;&nbsp;Get Stone Data
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Label className="mt-2">
                            Stone IDs: <sup style={{ color: "red" }}>*</sup>
                        </Form.Label>
                        <TextField
                            id="outlined-multiline-static"
                            label="Comma seperated Stone IDs (required)"
                            multiline
                            fullWidth
                            rows={4}
                            value={stoneIds}
                            onChange={(e) => {
                                setStoneIds(Utility.ConvertNewLineToComma(e.target.value));
                            }}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Box sx={{
                            "& .Mui-disabled": {
                                backgroundColor: "rgba(0, 0, 0, 0.12) !important",
                                color: "rgba(0, 0, 0, 0.26) !important"
                            }
                        }}>
                            <LoadingButton
                                loading={loadingButton}
                                loadingPosition="start"
                                startIcon={<SaveIcon />}
                                variant="contained"
                                sx={{ mr: 2 }}
                                onClick={() => {
                                    let values = [];
                                    stoneIds.replaceAll('\n', ',').replaceAll(' ', ',').split(',').forEach((e) => {
                                        if (e && e !== '') {
                                            values.push(e)
                                        }
                                    });
                                    handleGetStoneData(values);
                                }}
                                type="Submit"
                                disabled={stoneIds === ""}
                            >
                                Submit
                            </LoadingButton>
                        </Box>
                        <Button disabled={loadingButton} variant="contained" color="error" onClick={() => setShowGetStoneDataModal(false)}>
                            Close
                        </Button>
                    </Modal.Footer>
                </Modal>
            ) : null}
        </Box >
    )

}

export default Pricing;